import DropdownSheetHeader from 'LuxKit/Dropdown/Sheet/DropdownSheetHeader'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { ComponentProps } from 'react'
import React, { useContext, useMemo } from 'react'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import { getRecommendationsTitle } from './Recommendations/utils/recommendationsTitle'
import Group from 'components/utils/Group'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCalendarCapacitySelect from './CalendarCapacity/OfferCalendarCapacitySelect'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import OfferCalendarRoomSelect from './CalendarRooms/OfferCalendarRoomSelect'
import { isVillaOffer } from 'lib/offer/offerTypes'
import OfferCalendarDurationSelect from './CalendarDuration/OfferCalendarDurationSelect'
import GeoContext from 'contexts/geoContext'
import useIsFlightsDisabledForGeoState from 'hooks/Flights/useIsFlightsDisabledForGeoState'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import OfferCalendarFlightsToggle from './OfferCalendarFlightsToggle'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import OfferCalendarLuxPlusToggle from 'luxPlus/components/OfferCalendarLuxPlusToggle'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'

interface Props extends Pick<ComponentProps<typeof DropdownSheetHeader>, 'onBackClick' | 'onDismissClick'> {
  subFilters?: Array<OfferPageFilterType>
  offer: App.Offer
  duration?: number
  rooms?: Array<App.Occupants>
  monthIndex?: string
  recommendationsCheckIn?: string
  recommendationsMonthIndex?: string
  calendarRecommendationsEnabled?: boolean
  calendarRecommendationsState: App.ApiResponseState
  calendarRecommendationsOfferId?: Array<string>
  isMultipleRoomBookingActive: boolean
  isLuxPlusEnabled: boolean
}

function OfferCalendarDatePickerDropdownHeader(props: Props) {
  const {
    onBackClick,
    onDismissClick,
    offer,
    subFilters = EmptyArray,
    duration,
    rooms = EmptyArray,
    monthIndex,
    recommendationsCheckIn,
    recommendationsMonthIndex,
    calendarRecommendationsEnabled,
    calendarRecommendationsState,
    calendarRecommendationsOfferId,
    isMultipleRoomBookingActive,
    isLuxPlusEnabled,
  } = props

  const intl = useIntl()
  const { currentRegionHasFlights } = useContext(GeoContext)
  const isFlightsDisabledForGeoState = useIsFlightsDisabledForGeoState()

  const hasSelectedMonth = monthIndex !== undefined

  const occupanciesSum = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const flightsEnabled = currentRegionHasFlights && offer.offerFlightsEnabled && !offer.bundledWithFlightsOnly && occupanciesSum < 10 && !isFlightsDisabledForGeoState

  const capacities = useMemo(() => {
    return nonNullable(offer.packages.flatMap(pkg => pkg.roomRate?.capacities))
  }, [offer])

  const [recommendationTitle, recommendationSubtitle] = useMemo(() => {
    const readyToDisplay = calendarRecommendationsState === 'done' && !!calendarRecommendationsOfferId?.length
    if (calendarRecommendationsEnabled && readyToDisplay) {
      const { title, subtitle } = getRecommendationsTitle({
        checkIn: recommendationsCheckIn,
        monthIndex: recommendationsMonthIndex,
        duration: duration!,
        intl,
      })
      return [title, subtitle] as const
    }
    return [] as const
  }, [calendarRecommendationsEnabled, calendarRecommendationsOfferId?.length, calendarRecommendationsState, duration, recommendationsCheckIn, recommendationsMonthIndex, intl])

  const [dropdownTitle, dropdownSubtitle] = useMemo(() => {
    if (calendarRecommendationsEnabled) {
      return [
        recommendationTitle,
        recommendationSubtitle,
      ]
    }

    return [
      hasSelectedMonth
        ? intl.formatMessage({
            id: 'gptskx2IV80VHP3GarOU',
            defaultMessage: 'Select check-in date',
            description: 'Title for calendar date picker dropdown when month is selected',
          })
        : intl.formatMessage({
            id: 'Cc2DFql8xX-Q75cyGZ2G',
            defaultMessage: 'Select check-in month',
            description: 'Title for calendar date picker dropdown when selecting month',
          }),
      undefined,
    ] as const
  }, [calendarRecommendationsEnabled, hasSelectedMonth, recommendationTitle, recommendationSubtitle, intl])

  return <DropdownSheetHeader
    size="M"
    alignment={calendarRecommendationsEnabled ? 'center' : 'start'}
    title={dropdownTitle}
    subtitle={dropdownSubtitle}
    data-testid="offer-calendar-date-picker"
    actionsSlot={!calendarRecommendationsEnabled && <Group direction="horizontal" gap={8} wrap="wrap">
      {subFilters.includes('capacity') && <OfferCalendarCapacitySelect capacities={capacities} disableMultiRoom={isVillaOffer(offer)} />}
      {!isMultipleRoomBookingActive && subFilters.includes('room') && <OfferCalendarRoomSelect />}
      {subFilters.includes('duration') && <OfferCalendarDurationSelect />}
      {flightsEnabled && subFilters.includes('airport') && <OfferCalendarFlightsToggle />}
      {isLuxPlusEnabled && offer.luxPlus.hasMemberPrices && <OfferCalendarLuxPlusToggle />}
    </Group>}
    onDismissClick={onDismissClick}
    onBackClick={(hasSelectedMonth || calendarRecommendationsEnabled) ? onBackClick : undefined}
  />
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    offer: state.offer,
    duration: state.duration,
    rooms: state.rooms,
    recommendationsCheckIn: state.recommendationsCheckInDay,
    recommendationsMonthIndex: state.recommendationsMonthIndex,
    calendarRecommendationsEnabled: state.showRecommendationsView && shouldEnableCalendarRecommendations(state.offer),
    calendarRecommendationsState: appState.recommendations.calendarRecommendations.state,
    calendarRecommendationsOfferId: appState.recommendations.calendarRecommendations.offerIds,
    isMultipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
    isLuxPlusEnabled: getIsLuxPlusEnabled(appState),
    open: !!state.activeFilter,
  }
}, OfferCalendarDatePickerDropdownHeader, preventRenderWithClosedFilter)
